import React, { useEffect, useState } from "react";
import styled from "styled-components";
import AdminProfile from "../../components/AdminProfileTest";
import AdminEmail from "../../components/AdminEmail";
import AdminPassword from "../../components/AdminPassword";
import AdminNotice from "../../components/AdminNotification";
import { HOST_URL, reqOptions } from "../../assets/js/helpFunction";
import WithAuth from "../../components/partials/withAuth";
import {
  MssgContainer,
  getEstateID,
  getImage,
  titleCase,
} from "../../components/partials/utils";
import { SVGs } from "../../assets/svg/SVGs";
import "./admin-settings.css";

const SettingsIcon = ({ name, size = 18 }) => {
  const paths = {
    settings: "M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm7.4-3.5a7 7 0 0 0-.1-1l2-1.5-2-3.4-2.4 1a8 8 0 0 0-1.7-1L15 3.5h-4l-.3 2.6a8 8 0 0 0-1.7 1l-2.4-1-2 3.4 2 1.5a7 7 0 0 0 0 2l-2 1.5 2 3.4 2.4-1a8 8 0 0 0 1.7 1l.3 2.6h4l.3-2.6a8 8 0 0 0 1.7-1l2.4 1 2-3.4-2-1.5a7 7 0 0 0 .1-1Z",
    profile: "M20 21a8 8 0 0 0-16 0m8-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z",
    email: "M3 5h18v14H3V5Zm0 2 9 6 9-6",
    password: "M7 11V7a5 5 0 0 1 10 0v4m-11 0h12a2 2 0 0 1 2 2v8H4v-8a2 2 0 0 1 2-2Z",
    bell: "M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Zm-8 12h4",
    package: "M4 7 12 3l8 4-8 4-8-4Zm0 0v10l8 4 8-4V7m-8 4v10",
    chevron: "m9 18 6-6-6-6",
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d={paths[name]} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
};

const settingsSections = [
  { label: "Profile", description: "Personal and estate details", icon: "profile" },
  { label: "Email", description: "Your account email address", icon: "email" },
  { label: "Password", description: "Update account security", icon: "password" },
  { label: "Notifications", description: "Choose what reaches you", icon: "bell" },
  { label: "Packages", description: "Review available plans", icon: "package" },
];

const AdminSettings = () => {
  const [activeIndex, setActiveIndex] = useState(1);
  const handleClick = (index) => setActiveIndex(index);
  const checkActive = (index, className) =>
    activeIndex === index ? className : "";
  const [userdata, setUserData] = useState({});
  const [plans, setPlans] = useState([]);
  const [mssg, setMssg] = useState({ error: false, mssg: "" });
  const closeMssg = () => {
    setMssg({ error: false, mssg: "" });
  };

  const [openModal, setOpenModal] = useState(false);
  const [activePackage, setActivePackage] = useState(null);
  useEffect(() => {
    const req = reqOptions("GET", null, "access");
    const user_api =
      HOST_URL() +
      `estate_users/estate_user_detail/?estate_id=${getEstateID()}`;
    const plan_api = HOST_URL() + `estate_plans/`;
    Promise.all([fetch(user_api, req), fetch(plan_api, req)])
      .then(function (responses) {
        return Promise.all(
          responses.map(function (response) {
            return response.json();
          })
        );
      })
      .then(function (resp) {
        setUserData(resp[0]);
        setPlans(resp[1].results);
      })
      .catch(function (error) {
        setMssg({ ...mssg, mssg: error.message, error: true });
      });
  }, [getEstateID()]);
  return (
    <WithAuth>
      <main className="admin-settings-page">
      <header className="admin-settings-header">
        <div><p className="admin-settings-kicker"><SettingsIcon name="settings" /> ACCOUNT PREFERENCES</p><h1>Settings</h1><p>Manage your administrator profile, security and communication preferences.</p></div>
      </header>

      <section className="admin-settings-identity">
        <div className="admin-settings-avatar">
          {userdata?.profile_image ? <img src={getImage(userdata.profile_image)} alt="" /> : <span>{userdata?.estate_user?.user?.first_name?.[0]}{userdata?.estate_user?.user?.last_name?.[0]}</span>}
        </div>
        <div className="admin-settings-person"><h2>{userdata?.estate_user?.user?.first_name || "Estate"} {userdata?.estate_user?.user?.last_name || "Administrator"}</h2><p>{userdata?.estate_user?.user?.email || "Administrator account"}</p></div>
        <div className="admin-settings-estate"><span>Current estate</span><strong>{userdata?.estate_user?.estate?.name || "Estate workspace"}</strong><small>{userdata?.estate_user?.estate?.estate_id}</small></div>
      </section>

      <section className="admin-settings-workspace">
      <div className="event-container admin-settings-navigation">
        {mssg.mssg && <MssgContainer mssg={mssg} close={closeMssg} />}
        <HandleSearchAndTab>
          <div className="tabs">
            {settingsSections.map((section, index) => <span key={section.label} className={`tab ${checkActive(index + 1, "active")}`} onClick={() => handleClick(index + 1)}><span className="admin-settings-tab-icon"><SettingsIcon name={section.icon} /></span><button><strong>{section.label}</strong><small>{section.description}</small></button><SettingsIcon name="chevron" size={15} /></span>)}
          </div>
        </HandleSearchAndTab>
      </div>
      <div className="panels admin-settings-content">
        {userdata && (
          <>
            <div className={`panel ${checkActive(1, "active")}`}>
              <div className="admin-settings-panel-title"><h2>Profile information</h2><p>Keep your personal details and estate information up to date.</p></div>
              <AdminProfile
                userdata={userdata}
                setUserData={setUserData}
                setMssg={setMssg}
              />
            </div>
            <div className={`panel ${checkActive(2, "active")}`}>
              <div className="admin-settings-panel-title"><h2>Email address</h2><p>Review the email connected to your administrator account.</p></div>
              <AdminEmail
                userdata={userdata}
                setUserData={setUserData}
                setMssg={setMssg}
              />
            </div>
            <div className={`panel ${checkActive(3, "active")}`}>
              <div className="admin-settings-panel-title"><h2>Password &amp; security</h2><p>Use a strong, unique password to protect your estate account.</p></div>
              <AdminPassword setMssg={setMssg} />
            </div>
            <div className={`panel ${checkActive(4, "active")}`}>
              <div className="admin-settings-panel-title"><h2>Notification preferences</h2><p>Control which updates and reminders you receive.</p></div>
              <AdminNotice
                userdata={userdata}
                setUserData={setUserData}
                setMssg={setMssg}
              />
            </div>
            <div className={`panel ${checkActive(5, "active")}`}>
              <div className="admin-settings-panel-title"><h2>Estate packages</h2><p>Compare the plans available for your estate workspace.</p></div>
              <OverviewRap
                className="fourcard_grid all_overview"
                style={{ justifyContent: "left" }}
              >
                {plans.length
                  ? [...plans].reverse().map((plan) => (
                      <div
                        className="grid-row per_overview mx-2"
                        key={plan.id}
                        style={{ textAlign: "center" }}
                      >
                        <h3>{titleCase(plan.plan_type)}</h3>
                        <h5>N{plan.price}</h5>
                        <button
                          type="button"
                          className="admin-settings-plan-details"
                          onClick={() => {
                            setOpenModal(true);
                            setActivePackage(plan);
                          }}
                        >
                          See Details
                        </button>
                      </div>
                    ))
                  : ""}
              </OverviewRap>
            </div>
          </>
        )}
      </div>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        apackage={activePackage}
      />
      </section>
      </main>
    </WithAuth>
  );
};

export default AdminSettings;

const Modal = ({ open, onClose, apackage }) => {
  if (!open) return null;
  return (
    <div onClick={onClose} className="bills_on_me">
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="modalContainer"
      >
        <form className="new_estates_form fade-in-bck">
          <div className="form-group">
            <div className="create_est ">
              <div className="form_txt">
                <h3>Package Info</h3>
              </div>
              <label>
                <b>Name</b>
              </label>
              <p>{apackage && apackage.plan_type}</p>
              <label>
                <b>Description</b>
              </label>
              <p>{apackage && apackage.description}</p>
            </div>
            <button>Subscribe</button>
          </div>
        </form>
        <img src={SVGs.close} alt="" onClick={onClose} />
      </div>
    </div>
  );
};

const OverviewRap = styled.div`
  .mx-2 {
    margin: 10px 15px;
  }
  a {
    color: blue;
    cursor: pointer;
  }

  @media (min-width: 1320px) {
    .per_overview {
      width: 19.2%;
    }
  }
`;
const HandleSearchAndTab = styled.section`
  .container {
    margin-bottom: 20px;
  }
  .normal_tab {
    background: transparent;
  }
  .event-input {
    max-width: 675px;
  }
  @media (min-width: 760px) {
    display: flex;
    justify-content: space-between;
    .event-input {
      display: flex;
      justify-content: space-between;
      //   width: 57%;
      .select_me {
        margin-left: 10px;
        width: auto !important;
      }
      button {
        margin-top: 0;
        height: 45px;
      }
      .event-select {
        width: 20%;
      }
    }
  }
  @media (max-width: 540px) {
    .event-input {
      .event-select {
        display: none;
      }
    }
  }
  .tabs {
    margin: 0;

    .tab {
      margin: 0;
      height: 40px;
      align-items: center;
      display: flex;
      justify-content: center;
      padding: 10px;

      text-align: center;
      margin-right: 15px;
      border-bottom: 3px solid #c0c0c0;
      button {
        margin: 0;
        padding: 0 !important;
        padding: 5px 10px;
        //styleName: Web/Small Copy;

        font-size: 16px;
        font-weight: 400;
        line-height: 17px;
        letter-spacing: 0em;
        text-align: left;
      }
    }
    .active {
      background: transparent;
      border-bottom: 3px solid #2d4bf3;
      button {
        color: #2d4bf3;
      }
    }
  }
  .normal_tab.add_tape {
    border-bottom: 2px solid yellow;
  }
`;
